import * as React from 'react';
import { useEffect, useContext } from 'react';
import { Retrieve } from '@tff/types/tff';
import { RetrieveFlightBatchUpdateResponse } from '../models/PnrUpdateResponse';
import { LoadingContext } from '../contexts/LoadingContextProvider';
import { apigeeEndpoint } from '../apis';

export type UpdateRetrieveDbProps = {
  itemsToUpdate: Retrieve[];
  ticketTimeLimit: string;
};

export type TypeOfUpdateRetrieveDbStatus = 'IN_PROGRESS' | 'FINISH' | 'FAILURE';

export type RetrieveDbServiceResponse = {
  updateRetrieveResponse?: RetrieveFlightBatchUpdateResponse;
  updateRetrieveDbError?: any;
  updateRetrieveDbStatus?: TypeOfUpdateRetrieveDbStatus;
  resetUpdateRetrieveDbState: () => void;
};

export const useUpdateRetrieveDb = (updateRetrieveDbProps?: UpdateRetrieveDbProps): RetrieveDbServiceResponse => {
  const [updateRetrieveDbError, setUpdateRetrieveDbError] = React.useState(undefined);
  const [updateRetrieveResponse, setUpdateRetrieveResponse] = React.useState<
    RetrieveFlightBatchUpdateResponse | undefined
  >(undefined);
  const [updateRetrieveDbStatus, setUpdateRetrieveDbStatus] = React.useState<TypeOfUpdateRetrieveDbStatus | undefined>(
    undefined,
  );
  const { showLoading, closeLoading } = useContext(LoadingContext);

  const resetUpdateRetrieveDbState = (): void => {
    setUpdateRetrieveResponse(undefined);
    setUpdateRetrieveDbStatus(undefined);
    setUpdateRetrieveDbError(undefined);
  };

  useEffect(() => {
    const updateRetrievesAtBackend = async (itemsToUpdate: Retrieve[], ticketTimeLimit: string): Promise<void> => {
      try {
        showLoading(`Update items at retrieve DB`);
        setUpdateRetrieveDbStatus('IN_PROGRESS');

        const tfmPnrs = itemsToUpdate.map(retrieve => retrieve.tfmPnr);
        const payload = {
          tfmPnrs,
          ticketTimeLimit,
        };

        const response = await apigeeEndpoint.put(`/ticket-time-limit`, payload);

        // Check if response is an error response
        if (response.data.errorMessage) {
          setUpdateRetrieveResponse({
            Status: 'FAILURE',
            Error: response.data.errorMessage
          });
        } else {
          setUpdateRetrieveResponse(response.data);
        }
        setUpdateRetrieveDbStatus('FINISH');
      } catch (error) {
        setUpdateRetrieveDbStatus('FAILURE');
      } finally {
        closeLoading();
      }
    };

    if (
      updateRetrieveDbProps?.ticketTimeLimit &&
      updateRetrieveDbProps?.itemsToUpdate?.length &&
      !updateRetrieveDbStatus
    ) {
      const { itemsToUpdate, ticketTimeLimit } = updateRetrieveDbProps;
      void updateRetrievesAtBackend(itemsToUpdate, ticketTimeLimit);
    }
  }, [updateRetrieveDbProps, updateRetrieveDbStatus, showLoading, closeLoading]);

  return {
    updateRetrieveResponse,
    updateRetrieveDbStatus,
    updateRetrieveDbError,
    resetUpdateRetrieveDbState,
  };
};
